/**
 * Unified neighbor extraction for Word Drift pathfinding
 * Extracts all valid neighbors for a given word according to game rules
 */

import type { DictionaryEntry } from './DictionaryTypes';
import type { EmbeddingsManager } from './EmbeddingsManager';
import type { GameWord } from './GameTypes';
import { enhancedLemmatizeWord } from './WordUtils';
import { PATHFINDING_CONSTANTS } from './GameConstants';

/**
 * Get all valid neighbors for a word. The neighbors include:
 * 1. Lemmatized forms of current word
 * 2. All words in synset definitions (length > 2, exists in dictionary)
 * 3. All semantic relations for each synset
 * 4. CosineSimilarityBridges with value >= 0.75
 * 5. Goal word if its cosine similarity to current word >= 0.75
 * 6. Goal word if current word is in goal's neighbors and similarity >= 0.5
 *    (e.g., "bird" in "songbird" definition → "songbird" is neighbor of "bird")
 * 
 * @param word - The current word (doesn't need to be lemmatized)
 * @param dictionary - Dictionary with entries and relations
 * @param goalWord - Optional goal word to potentially include as neighbor
 * @param embeddingsManager - Optional embeddings for cosine similarity checks
 * @returns Array of neighbor GameWords (deduplicated by onClickDestination)
 */
export function getAllNeighbors(
  word: string,
  dictionary: Record<string, DictionaryEntry>,
  goalWord?: string,
  embeddingsManager?: EmbeddingsManager
): GameWord[] {
  const wordLower = word.toLowerCase();
  const entry = dictionary[wordLower];
  
  if (!entry) {
    // logger.debug(`[NeighborExtractor] No dictionary entry for "${word}"`);
    return [];
  }
  
  // Use Map to deduplicate by onClickDestination (the actual graph node to navigate to)
  const neighborsMap = new Map<string, GameWord>();
  
  // Helper to add neighbor with proper onClickDestination logic
  const addNeighbor = (display: string, lemmatized: string) => {
    const displayLower = display.toLowerCase();
    // Determine correct navigation destination: display if in dict, else lemmatized
    const destination = dictionary[displayLower] ? displayLower : lemmatized;
    
    // Skip self-references and duplicates
    if (destination !== wordLower && !neighborsMap.has(destination)) {
      neighborsMap.set(destination, {
        variations: {
          display,
          lemmatized,
          onClickDestination: destination
        }
        // gameplay field omitted - populated during actual gameplay
      });
    }
  };
  
  // 1. Add lemmatized forms of current word
  if (entry.lemmatizedForms) {
    entry.lemmatizedForms.forEach(form => {
      const lemma = form.toLowerCase();
      addNeighbor(form, lemma);
    });
  }
  
  // Process each synset
  entry.synset.forEach(synset => {
    // 3. Add semantic relations
    const relations = synset.semanticRelations;
    const semanticWords = [
      ...(relations.synonyms || []),
      ...(relations.hypernyms || []),
      ...(relations.holonyms || []),
      ...(relations.meronyms || []),
      ...(relations.troponyms || []),
      ...(relations.entailments || []),
      ...(relations.coordinateTerms || [])
    ];
    
    semanticWords.forEach(w => {
      const normalized = w.toLowerCase();
      addNeighbor(w, normalized);
    });
    
    // 2. Add words from definition text (NOT examples - excludes example-only words)
    const definitionWords = synset.definition
      .split(/[^a-zA-Z0-9]+/)
      .map(w => w.toLowerCase())
      .filter(w => w.length > PATHFINDING_CONSTANTS.MIN_WORD_LENGTH && w !== wordLower);
    
    definitionWords.forEach(displayWord => {
      // Check if word exists in dictionary (either as-is or lemmatized)
      if (dictionary[displayWord]) {
        // Word exists in dictionary as-is (e.g., "rock")
        addNeighbor(displayWord, displayWord);
      } else {
        // Word doesn't exist, try lemmatizing (e.g., "rocks" → "rock")
        const lemmatized = enhancedLemmatizeWord(displayWord, dictionary);
        if (lemmatized !== displayWord && dictionary[lemmatized]) {
          // Lemmatized form exists in dictionary
          // Keep original display form (e.g., display="rocks", lemmatized="rock")
          addNeighbor(displayWord, lemmatized);
        }
      }
    });
  });
  
  // 4. Add cosine similarity bridges (if embeddings available)
  if (embeddingsManager?.loaded && entry.cosineSimilarityBridges) {
    Object.entries(entry.cosineSimilarityBridges).forEach(([bridgeWord, similarity]) => {
      if (similarity >= PATHFINDING_CONSTANTS.MIN_COSINE_BRIDGE_THRESHOLD) {
        const normalized = bridgeWord.toLowerCase();
        addNeighbor(bridgeWord, normalized);
      }
    });
  }
  
  // 5. Add goal word if close enough (if embeddings available and goal provided)
  if (goalWord && embeddingsManager?.loaded) {
    const goalLower = goalWord.toLowerCase();
    if (goalLower !== wordLower) {
      const similarity = embeddingsManager.similarity(wordLower, goalLower);
      if (similarity !== null && similarity >= PATHFINDING_CONSTANTS.GOAL_WORD_INCLUSION_THRESHOLD) {
        addNeighbor(goalWord, goalLower);
        // logger.debug(`[NeighborExtractor] Adding goal word "${goalWord}" as neighbor for "${word}" (similarity: ${similarity.toFixed(3)})`);
      }
    }
  }
  
  // 6. Add goal word if current word is a neighbor of goal word and similarity >= 0.5
  // Example: "bird" appears in "songbird" definition, so when getting neighbors for "bird", include "songbird"
  if (goalWord && embeddingsManager?.loaded && dictionary[goalWord.toLowerCase()]) {
    const goalLower = goalWord.toLowerCase();
    if (goalLower !== wordLower && !neighborsMap.has(goalLower)) {
      // Get goal word's neighbors (without goal parameter to avoid infinite recursion)
      const goalNeighbors = getAllNeighbors(goalWord, dictionary, undefined, embeddingsManager);
      
      // Check if current word is one of goal's neighbors
      const isCurrentWordInGoalNeighbors = goalNeighbors.some(
        gw => gw.variations.onClickDestination === wordLower
      );
      
      // If current word is in goal's neighbors and similarity >= 0.5, add goal as neighbor
      if (isCurrentWordInGoalNeighbors) {
        const similarity = embeddingsManager.similarity(wordLower, goalLower);
        if (similarity !== null && similarity >= 0.5) {
          addNeighbor(goalWord, goalLower);
          // logger.debug(`[NeighborExtractor] Adding goal word "${goalWord}" as neighbor (current word "${word}" is in goal's neighbors, similarity: ${similarity.toFixed(3)})`);
        }
      }
    }
  }
  
  return Array.from(neighborsMap.values());
}
